import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User, UserService } from '../../../../../js/api';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../components/dialog';
import { Heading } from '../../../components/heading';
import { BooleanFilterConfig, ListFilter } from '../../../components/list-filter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { TooltippedText } from '../../../components/tooltipped-text';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { useRequireActingAsEntity } from '../../../hooks/useRequireActingAsEntity';
import { formatDate } from '../../../utils/date';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import {
  MAIN_CONTENT_AREA,
  MAIN_CONTENT_AREA_FIXED_STANDALONE,
  PAGE_HEADER,
  TABLE_SCROLL_BOX,
} from '../../../utils/layoutStyles';
import { EditUserPermissionsDialog } from './EditUserPermissionsDialog';
import { InviteUserDialog } from './InviteUserDialog';
import { canDeleteUser } from './roleDefaults';
import UserCard from './UserCard';

const PAGE_SIZE = 21;

type ApiParams = Parameters<typeof UserService.userList>[0];
const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  HealthcareEntityRepGroups: 'groupNamesIn',
  HealthcareEntityRepRole: 'roleIn',
};
const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

const BOOLEAN_FILTERS: BooleanFilterConfig[] = [
  {
    trueLabel: 'Active',
    falseLabel: 'Inactive',
    queryString: 'active',
    defaultValue: true,
  },
];

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  body: string;
  confirmText: string;
  confirmVariant?: 'danger' | 'primary';
  isLoading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmText,
  confirmVariant = 'primary',
  isLoading = false,
}) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>{title}</DialogTitle>
    <DialogBody>
      <p className="tw:text-zinc-700">{body}</p>
    </DialogBody>
    <DialogActions>
      <Button type="button" plain onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button
        type="button"
        color={confirmVariant === 'danger' ? 'stoplight-danger' : 'brandgreen1'}
        onClick={onConfirm}
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : confirmText}
      </Button>
    </DialogActions>
  </Dialog>
);

const ActionCell: React.FC<{
  user: User;
  queryKeyBase: string;
  showOrganization: boolean;
}> = ({ user, queryKeyBase, showOrganization }) => {
  const { user: currentUser } = useCurrentUser();
  const canDelete = canDeleteUser(currentUser);
  const queryClient = useQueryClient();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const canRead = useHasPermission('read__healthcare_entities__healthcareentityrep');
  const canEdit = useHasPermission('update__healthcare_entities__healthcareentityrep');

  const deactivateMutation = useMutation({
    mutationFn: async () => {
      await UserService.userDestroy({ id: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      await UserService.userReactivateUpdate({ id: user.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeyBase] });
    },
  });

  const handleDeactivate = async () => {
    try {
      await deactivateMutation.mutateAsync();
      toast.success('User deactivated successfully');
      setShowDeactivateDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateMutation.mutateAsync();
      toast.success('User reactivated successfully');
      setShowReactivateDialog(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  if (currentUser?.id === user.id) {
    return null;
  }

  return (
    <>
      <div className="tw:flex tw:justify-start tw:gap-2">
        {user.is_active && canRead && (
          <TooltippedText tooltipContent={canEdit ? 'Edit' : 'View'}>
            <Button
              small
              color="brand-green-outline"
              onClick={(e) => {
                e.stopPropagation();
                setShowEditDialog(true);
              }}
              title={canEdit ? 'Edit' : 'View'}
              data-testid={`user-edit-button-${user.id}`}
            >
              {canEdit ? <i className="far fa-fw fa-pencil" /> : <i className="far fa-fw fa-eye" />}
            </Button>
          </TooltippedText>
        )}
        {canDelete &&
          (user.is_active ? (
            <TooltippedText tooltipContent="Deactivate">
              <Button
                small
                color="stoplight-danger-outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeactivateDialog(true);
                }}
                title="Deactivate"
                data-testid={`user-deactivate-button-${user.id}`}
              >
                <i className="far fa-fw fa-trash-can" />
              </Button>
            </TooltippedText>
          ) : (
            <TooltippedText tooltipContent="Reactivate">
              <Button
                small
                color="zinc-outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReactivateDialog(true);
                }}
                title="Reactivate"
                data-testid={`user-reactivate-button-${user.id}`}
              >
                <i className="far fa-fw fa-user-plus" />
              </Button>
            </TooltippedText>
          ))}
      </div>

      <EditUserPermissionsDialog
        user={user}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        queryKeyBase={queryKeyBase}
        showOrganization={showOrganization}
      />

      <ConfirmationDialog
        open={showDeactivateDialog}
        onClose={() => setShowDeactivateDialog(false)}
        onConfirm={handleDeactivate}
        title="Deactivate User"
        body="Are you sure you want to deactivate this user?"
        confirmText="Deactivate"
        confirmVariant="danger"
        isLoading={deactivateMutation.isPending}
      />

      <ConfirmationDialog
        open={showReactivateDialog}
        onClose={() => setShowReactivateDialog(false)}
        onConfirm={handleReactivate}
        title="Reactivate User"
        body="Are you sure you want to reactivate this user?"
        confirmText="Reactivate"
        isLoading={reactivateMutation.isPending}
      />
    </>
  );
};

export type UsersListPageProps = {
  title?: string;
  queryKeyBase: string;
  displayTypePrefKey: string;
  pendingInvitationsRoute: string;
  showOrganization?: boolean;
  showOrganizationFilter?: boolean;
  linkContext?: 'organization' | 'staff';
};

export const UsersListPage: React.FC<UsersListPageProps> = ({
  title = 'Users',
  queryKeyBase,
  displayTypePrefKey,
  pendingInvitationsRoute,
  showOrganization = false,
  showOrganizationFilter = false,
}) => {
  useDocumentTitle(title);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const requireActingAs = useRequireActingAsEntity();
  const canInvite = useHasPermission('create__accounts__invitation');
  const canViewPendingInvitations = useHasPermission('list__accounts__invitation');

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
    booleanFilters: BOOLEAN_FILTERS,
  });

  const search = filterValues.search || '';
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const activeFilter = filterValues.booleanFilters?.active ?? true;
  const healthcareEntity = filterValues.healthcare_entity || '';
  const displayType = (filterValues.displayType || 'card') as 'card' | 'table';
  // Table view: the rows scroll in their own box under a sticky header (utils/layoutStyles).
  const tableScrolls = displayType !== 'card';
  const ordering = (filterValues.ordering || 'first_name') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        categories: filterValues.categories,
        booleanFilters: filterValues.booleanFilters,
        displayType,
        ordering: next,
        healthcare_entity: filterValues.healthcare_entity,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.categories,
      filterValues.booleanFilters,
      filterValues.healthcare_entity,
      displayType,
    ]
  );

  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  const queryKey = useMemo(
    () => [queryKeyBase, search, categoryFilters, activeFilter, ordering, healthcareEntity],
    [queryKeyBase, search, categoryFilters, activeFilter, ordering, healthcareEntity]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam = 0 }) => {
        return UserService.userList({
          active: activeFilter,
          search: search || undefined,
          healthcareEntityId: showOrganization && healthcareEntity ? healthcareEntity : undefined,
          ...categoryApiParams,
          ordering: ordering as ApiParams['ordering'],
          limit: PAGE_SIZE,
          offset: pageParam as number,
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.next) {
          return allPages.length * PAGE_SIZE;
        }
        return undefined;
      },
    });

  const users = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreUsers = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreUsers, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const { data: roleLabelMap = {} } = useCodeDefinitionsAsMap('HealthcareEntityRepRole');

  return (
    <main
      className={clsx(MAIN_CONTENT_AREA, tableScrolls && MAIN_CONTENT_AREA_FIXED_STANDALONE)}
      data-testid="users-list-page"
    >
      <div className={STICKY_HEADER}>
        <div className={PAGE_HEADER}>
          <Heading level={1} className="tw:!text-brand-green-0">
            <i className="far fa-fw fa-id-badge tw:mr-1" />
            {title}
          </Heading>
          <div className="tw:flex tw:gap-2">
            {canViewPendingInvitations && (
              <Button
                color="brandgreen1"
                onClick={() => navigate(pendingInvitationsRoute)}
                data-testid="users-pending-invitations-link"
              >
                Pending Invitations
              </Button>
            )}
            {canInvite && (
              <Button
                color="brandgreen1"
                onClick={() => requireActingAs(() => setShowInviteDialog(true))}
                data-testid="users-invite-button"
              >
                Invite User
              </Button>
            )}
            <InviteUserDialog open={showInviteDialog} onClose={() => setShowInviteDialog(false)} />
          </div>
        </div>

        {isError && (
          <div className="tw:mt-4 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700">
            <p className="tw:font-semibold">Something went wrong</p>
            <p className="tw:text-sm">
              Please try again later. If the problem persists, please contact us.
            </p>
          </div>
        )}

        <div className="tw:mt-1">
          <ListFilter
            show_search={true}
            categories={Array.from(CATEGORIES)}
            booleanFilters={BOOLEAN_FILTERS}
            showStatus={false}
            showReason={false}
            showOrganizationFilter={showOrganizationFilter}
            showDisplayType={true}
            labelOverrides={{
              HealthcareEntityRepGroups: 'Permissions',
              HealthcareEntityRepRole: 'Role',
            }}
            sortOptions={[
              ...(showOrganization
                ? [
                    {
                      label: 'Organization',
                      value: 'healthcare_entity_rep__healthcare_entity__name',
                    },
                  ]
                : []),
              { label: 'Name', value: 'first_name' },
              { label: 'Last Name', value: 'last_name' },
              { label: 'Email', value: 'email' },
              { label: 'Role', value: 'healthcare_entity_rep__role' },
              { label: 'Last Login', value: 'last_login' },
              { label: 'Account Created', value: 'healthcare_entity_rep__created_at' },
            ]}
            values={filterValues}
            onFilterChange={updateFilters}
          />
        </div>
      </div>

      <div className={clsx('tw:mt-3', tableScrolls && TABLE_SCROLL_BOX)}>
        {isLoading && users.length === 0 && (
          <div className="tw:text-center tw:py-8">Loading users...</div>
        )}
        {!isLoading && users.length === 0 ? (
          <div
            data-testid="users-empty"
            className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8"
          >
            No users found.
            <div className="tw:text-zinc-500 tw:italic tw:text-sm">
              Refine your search or invite a new user.
            </div>
          </div>
        ) : (
          <>
            {users.length > 0 && (
              <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                Showing {users.length} of {totalCount} users
              </div>
            )}

            {displayType === 'card' ? (
              <div className="tw:@container">
                <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                  {users.map((user) => (
                    <div key={user.id}>
                      <UserCard
                        user={user}
                        roleLabelMap={roleLabelMap}
                        showOrganization={showOrganization}
                        actions={
                          <ActionCell
                            user={user}
                            queryKeyBase={queryKeyBase}
                            showOrganization={showOrganization}
                          />
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <Table striped dense stickyHeader={tableScrolls}>
                <TableHead>
                  <TableSortHeader
                    sortKey="first_name"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Name
                  </TableSortHeader>
                  {showOrganization && (
                    <TableSortHeader
                      sortKey="healthcare_entity_rep__healthcare_entity__name"
                      ordering={ordering}
                      onOrderingChange={setOrdering}
                      data-testid="users-table-org-header"
                    >
                      Organization
                    </TableSortHeader>
                  )}
                  <TableSortHeader
                    sortKey="email"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Email
                  </TableSortHeader>
                  <TableSortHeader
                    sortKey="healthcare_entity_rep__role"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Role
                  </TableSortHeader>
                  <TableSortHeader
                    sortKey="last_login"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Last Login
                  </TableSortHeader>
                  <TableSortHeader
                    sortKey="healthcare_entity_rep__created_at"
                    ordering={ordering}
                    onOrderingChange={setOrdering}
                  >
                    Account Created
                  </TableSortHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow noPointer key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        {[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}
                      </TableCell>
                      {showOrganization && (
                        <TableCell data-testid={`user-row-org-${user.id}`}>
                          {user.healthcare_entity_name || '—'}
                        </TableCell>
                      )}
                      <TableCell>{user.email || '—'}</TableCell>
                      <TableCell>
                        {user.role ? roleLabelMap[user.role] || user.role : '—'}
                      </TableCell>
                      <TableCell>{formatDate(user.last_login)}</TableCell>
                      <TableCell>{formatDate(user.rep_created_at)}</TableCell>
                      <TableCell>{user.is_active ? 'Active' : 'Inactive'}</TableCell>
                      <TableCell>
                        <ActionCell
                          user={user}
                          queryKeyBase={queryKeyBase}
                          showOrganization={showOrganization}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {hasNextPage && (
              <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                {isFetchingNextPage && (
                  <div className="tw:text-center tw:text-zinc-500">Loading more users...</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};
